import { CircularProgress } from '@mui/material'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { isValidFen } from '../../../chess/fen'
import { createRootGameTree, getLastPosition } from '../../../chess/gameTree'
import { BasePositionFEN, EmptyBoardFEN } from '../../../chess/positionPresets'
import { Dialogs, useDialogs } from '../../../sharedComponents/src/Contexts/DialogsContext'
import JoinUs from '../../../sharedComponents/src/Dialogs/Forms/JoinUs'
import { LoginState } from '../../../sharedComponents/src/globalHeader/GlobalHeader'
import { analyticsManager } from '../../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { useStoreActions, useStoreState } from '../../../store/hooks'
import AboutBot from './components/AboutBot'
import BotsList from './components/BotsList'
import CustomPanel from './components/CustomPanel'
import updateSelectedBotData from './hooks/updateSelectedBotData'

const PlayBotsPanel: React.FC = () => {
    const botsExist = useStoreState((state) => state.matchMaker.bots.length > 0)
    const isBotsLoading = useStoreState((state) => state.matchMaker.isBotsLoading)
    const gSSocket = useStoreState((state) => state.gameView.socket)
    const playComputerFen = useStoreState((state) => state.analysisMode.playComputerFen)
    const searchOptions = useStoreState((state) => state.matchMaker.searchOptions.computerChallenge)
    const fullSearchOptions = useStoreState((state) => state.matchMaker.searchOptions)
    const openCustomBotPanel = useStoreState((state) => state.matchMaker.openCustomBotPanel)
    const connectionStatus = useStoreState((state) => state.matchMaker.connectionStatus)
    const userState = useStoreState((state) => state.userData.state)

    const setMatchData = useStoreActions((state) => state.gameView.setMatchData)
    const clearGSConnection = useStoreActions((state) => state.gameView.shutDownGSConnection)
    const sendAIChallenge = useStoreActions((state) => state.matchMaker.sendAIChallenge)
    const setSearchOptions = useStoreActions((state) => state.matchMaker.setSearchOptions)
    const setOpenCustomBotPanel = useStoreActions((state) => state.matchMaker.setOpenCustomBotPanel)
    const setPlayComputerFen = useStoreActions((state) => state.analysisMode.setPlayComputerFen)
    const setGameTree = useStoreActions((state) => state.gameView.setGameTree)
    const setCurrentPositionId = useStoreActions((state) => state.gameView.setCurrentPositionId)

    const [playDisabled, setPlayDisabled] = useState<boolean>(true)
    const [validFen, setValidFen] = useState<boolean>(true)
    const { registerDialog } = useDialogs()

    const navigate = useNavigate()

    const setupBoard = (fen: string) => {
        const newGameTree = createRootGameTree(fen)
        setGameTree(newGameTree)
        const currentPositionId = getLastPosition(newGameTree).id
        setCurrentPositionId(currentPositionId)
    }

    // reset board on unmount
    useEffect(() => {
        return () => {
            setupBoard(BasePositionFEN)
        }
    }, [])

    // reset selected bot data on close custom bot panel
    useEffect(() => {
        if (searchOptions.selectedBot && openCustomBotPanel === false) {
            const updatedSearchOptions = updateSelectedBotData(fullSearchOptions, searchOptions.selectedBot)
            setSearchOptions(updatedSearchOptions)
            setPlayComputerFen(searchOptions.selectedBot.startingFEN || BasePositionFEN)
        }
    }, [openCustomBotPanel])

    // set up board on playComputerFen change
    useEffect(() => {
        setTimeout(() => {
            if (playComputerFen && isValidFen(playComputerFen)) {
                setupBoard(playComputerFen)
            } else {
                setValidFen(false)
                setupBoard(EmptyBoardFEN)
            }
        }, 1)
    }, [playComputerFen])

    // validation for play button
    useEffect(() => {
        if (botsExist && searchOptions.minutes > 0 && searchOptions.increment >= 0 && validFen) {
            setPlayDisabled(false)
        } else {
            setPlayDisabled(true)
        }
    }, [
        botsExist,
        connectionStatus,
        searchOptions.customTime,
        searchOptions.minutes,
        searchOptions.increment,
        playComputerFen,
        openCustomBotPanel,
        validFen,
    ])

    // create game on play button click
    const send = () => {
        if (searchOptions.selectedBot?.isLocked) {
            registerDialog(Dialogs.JOIN_US, <JoinUs />)
        } else {
            gSSocket?.close()
            clearGSConnection()
            gSSocket?.addEventListener('close', () => {
                setMatchData({
                    matchId: '',
                    gameServiceUrl: '',
                })
            })

            let customMinutes = searchOptions.minutes
            let selectedMinutes = searchOptions.selectedChallenge
                ? Number(searchOptions.selectedChallenge.split(',')[0])
                : null
            let customIncrement = searchOptions.increment
            let selectedIncrement = searchOptions.selectedChallenge
                ? Number(searchOptions.selectedChallenge.split(',')[1])
                : null

            sendAIChallenge({
                color: searchOptions.color,
                fen: playComputerFen,
                strength: searchOptions.strength,
                time: searchOptions.customTime ? customMinutes : selectedMinutes!,
                increment: searchOptions.customTime ? customIncrement : selectedIncrement!,
                challengeeId: searchOptions.selectedBot?.id,
                useTimeControl: searchOptions.useTimeControl,
                navigate: navigate,
            })

            analyticsManager.dispatchEvent('botChallenge', {
                minutes: searchOptions.customTime ? customMinutes : selectedMinutes!,
                increment: searchOptions.customTime ? customIncrement : selectedIncrement!,
                difficulty: searchOptions.strength,
                color: searchOptions.color === 'no_color' ? 'noColor' : searchOptions.color,
                botName: searchOptions.selectedBot?.name || 'Stockfish',
                botId: searchOptions.selectedBot?.id || '', // Keep for backwards compatibility
                botID: searchOptions.selectedBot?.id || '', // GTM expects botID
                startingFEN: playComputerFen,
            })

            // GTM also expects computerChallenge as a separate event
            analyticsManager.dispatchEvent('computerChallenge', {
                minutes: searchOptions.customTime ? customMinutes : selectedMinutes!,
                increment: searchOptions.customTime ? customIncrement : selectedIncrement!,
                difficulty: searchOptions.strength,
                color: searchOptions.color === 'no_color' ? 'noColor' : searchOptions.color,
                gameType: selectedMinutes! <= 3 ? 'bullet' : selectedMinutes! <= 10 ? 'blitz' : selectedMinutes! <= 30 ? 'rapid' : 'classic',
            })

            navigate('/')
        }
    }

    return (
        <Stack gap={1.5} sx={{ flexGrow: 1, minHeight: 0 }}>
            <Stack sx={{ flexGrow: 1, minHeight: 0 }}>
                {botsExist ? (
                    openCustomBotPanel ? (
                        <CustomPanel validFen={validFen} setValidFen={setValidFen} />
                    ) : (
                        <Stack sx={{ flexGrow: 1, minHeight: 0, justifyContent: 'space-between', overflow: 'hidden' }}>
                            <BotsList />
                            <AboutBot />
                        </Stack>
                    )
                ) : (
                    <Stack gap={1} sx={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center' }}>
                        {isBotsLoading ? (
                            <CircularProgress style={{ width: '100px', height: '100px' }} />
                        ) : (
                            <>
                                <Typography variant="h6">No bots available</Typography>
                                <Typography variant="body2">
                                    {userState !== LoginState.LOGGED_IN
                                        ? 'Please sign up to play bots'
                                        : 'Please try again later'}
                                </Typography>
                            </>
                        )}
                    </Stack>
                )}
            </Stack>
            <Stack gap={1} direction="row">
                <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setOpenCustomBotPanel(!openCustomBotPanel)}
                    disabled={!botsExist}
                >
                    {openCustomBotPanel ? 'Cancel' : 'Custom'}
                </Button>
                <Button variant={'contained'} fullWidth onClick={send} disabled={playDisabled}>
                    {searchOptions.selectedBot?.isLocked ? 'Unlock' : 'Play'}
                </Button>
            </Stack>
        </Stack>
    )
}

PlayBotsPanel.displayName = 'PlayBotsPanel'

export default PlayBotsPanel
